/**
 * This file holds actions. The actions commit mutations and can perform asynchronous operations.
 * Please refer to https://vuex.vuejs.org/guide/actions.html for more information
 */
import http from "@/http";
import { GDMState, IGraphic, GraphicComponent, GraphicConfig, IGraphicGDM, ApparelProduct } from "@/libs/graphics/types/Graphics.t";
import { AxiosResponse } from "axios";
import { ActionContext, ActionTree } from "vuex";
import { MutationsGDM } from "./mutations";
const merge = require('deepmerge');
import { ActionTypesGDM, MutationTypesGDM } from "./types";
const { VUE_APP_ENV: env, VUE_APP_ENDPOINTS: base } = process.env;

type AugmentedActionContextGDM = {
  commit<K extends keyof MutationsGDM>(
    key: K,
    payload: Parameters<MutationsGDM[K]>[1]
  ): ReturnType<MutationsGDM[K]>
} & Omit<ActionContext<GDMState, any>, 'commit'>

export interface ActionsGDM {
  [ActionTypesGDM.GET_GDM_CONFIG](
    { commit }: AugmentedActionContextGDM,
    payload: GraphicConfig
  ): Promise<GraphicConfig>

  [ActionTypesGDM.GET_GDM](
    { commit, state }: AugmentedActionContextGDM,
    payload: IGraphic
  ): IGraphicGDM | Promise<IGraphicGDM>
}

const actions: ActionTree<GDMState, any> & ActionsGDM = {
  [ActionTypesGDM.GET_GDM_CONFIG]({ commit, rootGetters }, { setData = true }) {
    const rootState = this.state as any
    const { caid, pdm } = rootState.utils;
    const domain: string = (rootState.utils.configuration.domain) ? rootState.utils.configuration.domain : process.env.VUE_APP_DOMAIN;
    const { env: utilsEnv } = rootState.utils;
    const url = `${domain}${base.replace(/\{ENV\}/g, utilsEnv)}/configurations/${caid}/configuration-graphics.json`;
    return new Promise((resolve, reject) => http
      .get(rootGetters['utils/addCacheBusting'](url))
      .then((response: AxiosResponse<GraphicConfig>) => {
        const data = response.data
        const sku: ApparelProduct = pdm?.sku
        if (sku) {
          const availableSwatchesForProduct = (response.data as GraphicConfig)?.[sku]
          data.global = merge(data.global, availableSwatchesForProduct ?? {});
        }
        if (setData) commit(MutationTypesGDM.SET_GDM_CONFIG, data);
        resolve(data);
      })
      .catch(reject)
    );
  },

  [ActionTypesGDM.GET_GDM]({ commit, state, rootGetters }, { setData = true, id = 10001, component3D = 'graphic_front' }) {
    const rootState = this.state as any
    const found = state.data?.
      find(data => data.component3D === component3D && data.id === id);
    if (found) return found;

    const domain = (rootState.utils.configuration.domain) ? rootState.utils.configuration.domain : process.env.VUE_APP_DOMAIN;
    const { env: utilsEnv } = rootState.utils;

    //TODO REMOVE ID REWRITE WHEN PDM IS UPDATED WITH DEFAULT ID
    id = Number(id) < 10000 ? 10001 : id

    const gdmURL = `${domain}${base.replace(/\{ENV\}/g, utilsEnv)}/graphics/${id}.json`;

    return new Promise((resolve, reject) => http
      .get(rootGetters['utils/addCacheBusting'](gdmURL))
      .then((response: AxiosResponse<IGraphicGDM>) => {
        response.data.component3D = component3D;
        const gdm = rootState.gdm as GDMState
        const componentIdx = gdm && gdm.data.findIndex(el => el.component3D === response.data.component3D);

        response.data.component.forEach((dataComponent, index) => {
          const gdmConfig: GraphicComponent = gdm.config[0] && gdm.config[0].component.find(component => component.id === dataComponent.id) || ({} as GraphicComponent);
          const mergedData = merge(gdmConfig, dataComponent);
          response.data.component[index] = mergedData;
        });

        if (setData && componentIdx >= 0) {
          commit(MutationTypesGDM.UPDATE_GDM, { componentIdx, data: response.data });
        }
        else {
          if (setData) commit(MutationTypesGDM.SET_GDM, response.data);
        }
        resolve(response.data);
      })
      .catch(reject)
    );

  }
}

export default actions;
